// based on https://mui.com/material-ui/react-autocomplete/#githubs-picker

import { ChangeEvent, KeyboardEvent, SyntheticEvent, useEffect, useRef } from 'react'

import {
  Autocomplete,
  autocompleteClasses,
  AutocompleteCloseReason,
  Box,
  Paper,
  Popper,
  Typography
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { CircleFlag } from '@/components/CircleFlag'
import { countriesAndTerritories, CountryTerritory } from '@/constants/countriesAndTerritories'

import { ConsoleTextField } from '../StyledInputComponents'

type CountryPopperProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onCountrySelect: (country: CountryTerritory) => void
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
    borderTopRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    width: 'calc(100% + 1rem)'
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    maxHeight: '286px',
    overflowY: 'auto',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    paddingBottom: '.5rem',
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'center',
      width: '100%'
    }
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative'
  }
}))

type PopperComponentProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const PopperComponent = (props: PopperComponentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal: _1, anchorEl, open: _2, ...other } = props
  return <StyledAutocompletePopper {...other} key={anchorEl?.id} />
}

export const CountryNumberPrefixSelectPopper = ({
  anchorEl,
  open,
  onClose,
  onCountrySelect
}: CountryPopperProps) => {
  const popperRef = useRef<HTMLDivElement>(null)

  const handleCountryChange = (
    _event: SyntheticEvent,
    value: CountryTerritory[],
    reason: string
  ) => {
    if (value && reason === 'selectOption') {
      onCountrySelect(value[0])
    }
  }

  const handleAutocompleteClose = (
    _event: ChangeEvent<unknown>,
    reason: AutocompleteCloseReason
  ) => {
    if (reason === 'escape' || reason === 'blur') {
      onClose()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (popperRef.current && popperRef.current.contains(target)) {
        return
      }

      if (anchorEl && anchorEl.contains(target)) {
        return
      }

      onClose()
    }

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose, anchorEl])

  return (
    <Popper
      ref={popperRef}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      disablePortal={false}
      style={{
        width: anchorEl?.offsetWidth || 320
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        }
      ]}
      sx={{
        zIndex: ({ zIndex }) => zIndex.modal,
        fontSize: 13,
        width: 'auto',
        minWidth: 320,
        borderRadius: ({ shape }) => shape.borderRadius
      }}
    >
      <Paper elevation={3} onKeyDown={handleKeyDown}>
        <Autocomplete
          open
          value={[]}
          multiple
          onChange={handleCountryChange}
          onClose={handleAutocompleteClose}
          autoFocus
          disableClearable
          autoComplete={false}
          options={countriesAndTerritories}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
          renderOption={(props, option) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 2
              }}
              {...props}
              key={option.iso2}
              component="li"
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircleFlag countryCode={option.iso2} height="100%" width="100%" />
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0
                }}
              >
                <Typography>{option.name}</Typography>
              </Box>

              <Typography
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 13,
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                +{option.dialCode}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <ConsoleTextField
              {...params}
              placeholder="Search countries..."
              autoFocus
              sx={{ px: 1, my: 1 }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: null
                }
              }}
            />
          )}
          slots={{
            popper: PopperComponent
          }}
          filterOptions={(options, { inputValue }) => {
            return options.filter(
              (option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.dialCode.includes(inputValue) ||
                option.iso2.toLowerCase().includes(inputValue.toLowerCase())
            )
          }}
        />
      </Paper>
    </Popper>
  )
}
